import { deserializeSuperjson } from '@lib/superjson';
import { ThunkAction, UnknownAction } from '@reduxjs/toolkit';
import {
  QueryActionCreatorResult,
  QueryArgFrom,
  QueryDefinition,
  StartQueryActionCreatorOptions,
} from '@reduxjs/toolkit/query';

import { api, type ApiTagTypes } from '../api';
import { BaseQuery, EndpointBuilderType, EndpointFunctionType } from './types';

// @NOTE(NT): This is a copy of the RTK type of the same name. It is not exported.
export type StartQueryActionCreator<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  D extends QueryDefinition<any, any, any, any, any>,
> = (
  arg: QueryArgFrom<D>,
  options?: StartQueryActionCreatorOptions,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
) => ThunkAction<QueryActionCreatorResult<D>, any, any, UnknownAction>;

export type QueryReturnType<Input, Result> = QueryDefinition<
  Input,
  BaseQuery,
  ApiTagTypes,
  Result,
  'api'
>;

function createQuery<Input, Result>(
  builder: EndpointBuilderType,
  query: EndpointFunctionType<Input>,
): QueryReturnType<Input, Result> {
  return builder.query<Result, Input>({
    query,
    transformResponse: deserializeSuperjson,
  });
}

/**
 * Creates and injects a new query endpoint into the api slice.
 *
 * @param name The name of the query hook and endpoint.
 * @param query The query function that will be used to generate the query hook and endpoint.
 * @returns A tuple containing the endpoint and the useQuery hook.
 */
export function injectQuery<Input, Result>(
  name: string,
  query: EndpointFunctionType<Input>,
) {
  const injectedApi = api.injectEndpoints({
    endpoints: (builder) => ({
      [name]: createQuery<Input, Result>(builder, query),
    }),
    overrideExisting: false,
  });

  const endpoint = injectedApi.endpoints[name];
  const useQuery = endpoint.useQuery;

  return [endpoint, useQuery] as const;
}
